import moment from 'moment/moment';
import { parseAtValue } from '#app/helpers/dateFieldTransforms';
import { ActiveGoalConstants, Color, FixDate } from 'finhelper';
import {
  GoalItem,
  GoalDataPeriod,
  PreparedChartDataItem,
  Client,
} from '../types/goalTimelineTypes';

/**
 * Подготовка данных целей для отображения на графике
 *
 * ИЗМЕНЕНИЯ:
 * 1. Добавлена поддержка типов TypeScript
 * 2. Исправлена логика извлечения дат: для целей с несколькими периодами
 *    теперь берется первый период для start_at и ПОСЛЕДНИЙ период для end_at
 * 3. Это важно, так как цель может состоять из нескольких последовательных периодов,
 *    и график должен показывать весь диапазон от начала первого до конца последнего периода
 */
export function prepareChartData(
  data: GoalItem[] | null | undefined,
  client: Client,
  filterCompleted: boolean = false,
  showPercents: boolean = false
): PreparedChartDataItem[] {
  if (!data || typeof data.forEach !== 'function') return [];

  let preparedData: PreparedChartDataItem[] = [];
  let colorIndex = 0;

  data.forEach((item: GoalItem) => {
    // Фильтр по типам целей
    if (
      [
        ActiveGoalConstants.SHORT,
        ActiveGoalConstants.MIDDLE,
        ActiveGoalConstants.LONG,
        ActiveGoalConstants.RETIRE,
      ].indexOf(item.type_id) === -1
    ) {
      return;
    }

    // Фильтр завершенных целей
    if (filterCompleted) {
      const percent = calculatePercent(item);
      if (percent >= 100) return;
    }

    // Структура данных может быть в разных форматах:
    // 1. data - строка JSON: "[{...}]"
    // 2. data - массив объектов: [{start_at: "...", end_at: "...", ...}]
    // 3. data - объект: {start_at: "...", end_at: "...", ...}

    let startDateStr: string | null = null;
    let endDateStr: string | null = null;
    let periods: GoalDataPeriod[] = [];

    // Проверяем, является ли data строкой JSON
    if (typeof item.data === 'string') {
      try {
        const parsedData = JSON.parse(item.data);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          periods = parsedData;
          // Берем первый период для начала и ПОСЛЕДНИЙ период для конца
          startDateStr = periods[0].start_at || null;
          endDateStr = periods[periods.length - 1].end_at || null;
        } else if (parsedData && typeof parsedData === 'object') {
          // Если это не массив, но объект
          startDateStr = parsedData.start_at || null;
          endDateStr = parsedData.end_at || null;
        }
      } catch (e) {
        console.warn(`[prepareChartData] Goal ${item.id}: Error parsing JSON data:`, e);
      }
    }
    // Если data - массив объектов
    else if (Array.isArray(item.data) && item.data.length > 0) {
      periods = item.data;
      // Берем первый период для начала и ПОСЛЕДНИЙ период для конца
      startDateStr = periods[0].start_at || null;
      endDateStr = periods[periods.length - 1].end_at || null;
    }
    // Если data - объект
    else if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
      const singlePeriod = item.data as GoalDataPeriod;
      startDateStr = singlePeriod.start_at || null;
      endDateStr = singlePeriod.end_at || null;
    }

    // Fallback: в демо/старых данных в item.data может не быть start_at у периодов — берём с уровня цели
    const goalItem = item as GoalItem & {
      start_at?: string;
      end_at?: string;
      start_at?: string;
      end_at?: string;
    };
    if (!startDateStr && (goalItem.start_at || goalItem.start_at)) {
      startDateStr = goalItem.start_at || goalItem.start_at || null;
    }
    if (!endDateStr && (goalItem.end_at || goalItem.end_at)) {
      endDateStr = goalItem.end_at || goalItem.end_at || null;
    }
    // Если период в data имеет только end_at — используем его для endDateStr
    if (
      !endDateStr &&
      Array.isArray(item.data) &&
      item.data.length > 0 &&
      (item.data[0] as GoalDataPeriod).end_at
    ) {
      endDateStr = (item.data[item.data.length - 1] as GoalDataPeriod).end_at || null;
    }
    if (
      !startDateStr &&
      Array.isArray(item.data) &&
      item.data.length > 0 &&
      (item.data[0] as GoalDataPeriod).start_at
    ) {
      startDateStr = (item.data[0] as GoalDataPeriod).start_at || null;
    }

    // Если данные не найдены, пропускаем цель
    if (!startDateStr || !endDateStr || startDateStr === '0' || endDateStr === '0') {
      console.warn(`[prepareChartData] Goal ${item.id}: missing date data`);
      return;
    }

    // Парсим дату (в т.ч. DD.MM.YYYY из демо/старых данных)
    const dateFormats = [
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD',
      'DD-MM-YYYY',
      'DD.MM.YYYY',
      'DD.MM.YYYY HH:mm:ss',
    ];
    let startDate = moment(startDateStr, dateFormats, true);
    let endDate = moment(endDateStr, dateFormats, true);

    // Если не удалось распарсить, пробуем стандартный формат
    if (!startDate.isValid() && startDateStr) {
      startDate = moment(startDateStr);
    }
    if (!endDate.isValid() && endDateStr) {
      endDate = moment(endDateStr);
    }

    if (!startDate.isValid() || !endDate.isValid()) {
      console.error(`[prepareChartData] Failed to parse dates for goal ${item.id}:`, {
        startDateStr,
        endDateStr,
        startDateValid: startDate.isValid(),
        endDateValid: endDate.isValid(),
      });
      return; // Пропускаем цель, если даты невалидны
    }

    if (item.type_id === ActiveGoalConstants.RETIRE && client?.retired_age != null) {
      const retireEnd = parseAtValue(client?.birth_at)?.add(client.retired_age, 'year');
      if (retireEnd?.isValid()) endDate = retireEnd;
    }

    // Выбор цвета
    if (typeof Color.colors[colorIndex] === 'undefined') {
      colorIndex = 0;
    }
    const color = Color.colors[colorIndex].color;

    // Имя цели
    const name = item.type_id === ActiveGoalConstants.RETIRE ? 'Пенсия' : item.name;

    // Создание элемента данных
    const dataItem: PreparedChartDataItem = {
      id: item.id,
      name: name,
      fromDate: startDate.format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
      color: color,
    };

    // Добавление процентов если нужно
    if (showPercents) {
      const percent = calculatePercent(item);
      dataItem.percent = percent < 0 ? 0 : percent;

      if (percent >= 100) {
        dataItem.color = Color.increaseBrightness(color, 70);
        dataItem.percent = 100;
      }
    }

    preparedData.push(dataItem);
    colorIndex++;
  });

  return preparedData;
}

/**
 * Расчет процента выполнения цели
 */
export function calculatePercent(item: GoalItem): number {
  let allPaymentsSum = 0;

  // Новая структура: payments.aim_payments_values - массив сумм платежей
  if (item.payments?.aim_payments_values && Array.isArray(item.payments.aim_payments_values)) {
    item.payments.aim_payments_values.forEach((paymentSum) => {
      const sum = parseFloat(String(paymentSum)) || 0;
      allPaymentsSum += sum;
    });
  }

  // Старая структура: goal_payments - массив объектов с paid_sum
  if (item.goal_payments && Array.isArray(item.goal_payments)) {
    item.goal_payments.forEach((payment) => {
      const sum = parseFloat(String(payment.paid_sum)) || 0;
      allPaymentsSum += sum;
    });
  }

  // Новая структура: future_sum может быть в разных форматах
  let futureSum = 0;

  // Если data - строка JSON
  if (typeof item.data === 'string') {
    try {
      const parsedData = JSON.parse(item.data) as GoalDataPeriod[];
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        // Суммируем future_sum из всех периодов
        parsedData.forEach((period) => {
          const periodSum = parseFloat(period.future_sum || '0') || 0;
          futureSum += periodSum;
        });
      }
    } catch (e) {
      console.warn('[calculatePercent] Error parsing JSON data:', e);
    }
  }
  // Если data - массив объектов
  else if (Array.isArray(item.data) && item.data.length > 0) {
    // Проверяем, есть ли future_sum в элементах (новая структура)
    if (item.data[0].future_sum !== undefined) {
      // Суммируем future_sum из всех периодов
      item.data.forEach((period: GoalDataPeriod) => {
        const periodSum = parseFloat(period.future_sum || '0') || 0;
        futureSum += periodSum;
      });
    } else {
      // Старая структура: суммируем row.sum
      item.data.forEach((row: any) => {
        const sum = parseFloat(String(row.sum)) || 0;
        if (sum > 0) {
          futureSum += sum;
        }
      });
    }
  }
  // Если data - объект
  else if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
    const singlePeriod = item.data as GoalDataPeriod;
    futureSum = parseFloat(singlePeriod.future_sum || '0') || 0;
  }

  const startSum = parseFloat(String(item.start_sum)) || 0;

  if (futureSum > 0) {
    const sum = allPaymentsSum + startSum;
    const diff = futureSum - sum;
    const percent = 100 - (diff / futureSum) * 100;
    const finalPercent = Math.max(0, Math.min(100, percent)); // Ограничиваем от 0 до 100
    return finalPercent;
  }

  return 100;
}

/**
 * Получение граничных дат для всех целей
 */
export function getBoundaryDates(
  data: GoalItem[] | null | undefined,
  client: Client
): { earlierDate: moment.Moment; olderDate: moment.Moment } {
  let earlierDate: moment.Moment | null = null;
  let olderDate: moment.Moment | null = null;

  if (data && typeof data.forEach === 'function') {
    data.forEach((item: GoalItem) => {
      let startDateStr: string | null = null;
      let endDateStr: string | null = null;
      let periods: GoalDataPeriod[] = [];

      // Проверяем, является ли data строкой JSON
      if (typeof item.data === 'string') {
        try {
          const parsedData = JSON.parse(item.data) as GoalDataPeriod[];
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            periods = parsedData;
            startDateStr = periods[0].start_at || null;
            endDateStr = periods[periods.length - 1].end_at || null;
          }
        } catch {
          // Игнорируем ошибки парсинга
        }
      }
      // Если data - массив объектов
      else if (Array.isArray(item.data) && item.data.length > 0) {
        periods = item.data;
        startDateStr = periods[0].start_at || null;
        endDateStr = periods[periods.length - 1].end_at || null;
      }
      // Если data - объект
      else if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
        const singlePeriod = item.data as GoalDataPeriod;
        startDateStr = singlePeriod.start_at || null;
        endDateStr = singlePeriod.end_at || null;
      }

      // Парсим дату, поддерживая оба формата
      let startDate = moment(
        startDateStr,
        ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', 'DD-MM-YYYY'],
        true
      );
      let endDate = moment(endDateStr, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', 'DD-MM-YYYY'], true);

      // Если не удалось распарсить, пробуем стандартный формат
      if (!startDate.isValid() && startDateStr) {
        startDate = parseAtValue(startDateStr);
      }
      if (!endDate.isValid() && endDateStr) {
        endDate = moment(endDateStr);
      }

      if (item.type_id === ActiveGoalConstants.RETIRE && client?.retired_age != null) {
        const retireEnd = parseAtValue(client?.birth_at)?.add(client.retired_age, 'year');
        if (retireEnd?.isValid()) endDate = retireEnd;
      }

      if (startDate.isValid()) {
        if (!earlierDate || startDate.isBefore(earlierDate)) {
          earlierDate = startDate;
        }
      }

      if (endDate.isValid()) {
        if (!earlierDate || endDate.isBefore(earlierDate)) {
          earlierDate = endDate;
        }

        if (!olderDate || endDate.isAfter(olderDate)) {
          olderDate = endDate;
        }
      }
    });
  }

  if (!earlierDate) {
    // @ts-ignore // TODO: fix типизацию FixDate.getNowDate
    earlierDate = FixDate.getNowDate(client, client.tutorial);
  }

  if (!olderDate) {
    // @ts-ignore  // TODO: fix типизацию FixDate.getNowDate
    olderDate = FixDate.getNowDate(client, client.tutorial).add(5, 'years');
  } else {
    olderDate = moment(olderDate).add(5, 'years');
  }

  return {
    // @ts-ignore // TODO: fix типизацию FixDate.getNowDate
    earlierDate: earlierDate || FixDate.getNowDate(client, client.tutorial),
    // @ts-ignore // TODO: fix типизацию FixDate.getNowDate
    olderDate: olderDate || FixDate.getNowDate(client, client.tutorial).add(5, 'years'),
  };
}

// ============================================
// helpers/chartElements.js
// ============================================

export function addCurrentDateMarker(dateAxis: any, am4core: any): void {
  const range = dateAxis.axisRanges.create();
  range.date = new Date();
  range.grid.stroke = am4core.color('#4378FF');
  range.grid.strokeWidth = 2;
  range.grid.strokeOpacity = 1;
  range.label.text = 'Сегодня';
  range.label.fontWeight = '600';
  range.label.fill = am4core.color('#000');
  range.label.valign = 'top';
  range.label.dy = -30;
  range.label.adapter.add('horizontalCenter', () => 'middle');
  range.label.align = 'center';
  range.label.inside = true;
  range.label.rotation = 0;
}

export function setupScrollbar(chart: any, am4core: any): void {
  chart.scrollbarX = new am4core.Scrollbar();
  chart.scrollbarX.parent = chart.chartAndLegendContainer;
  chart.scrollbarX.startGrip.disabled = true;
  chart.scrollbarX.endGrip.disabled = true;
  chart.scrollbarX.minHeight = 8;
}

export function setupCategoryLabels(categoryAxis: any, showPercents: boolean): void {
  categoryAxis.renderer.labels.template.adapter.add(
    'htmlOutput',
    function (text: string, item: any) {
      if (text === '') return '';

      let html = typeof text === 'string' ? text.charAt(0).toUpperCase() + text.slice(1) : '';
      let color = '';
      let mainColor = '';
      let percentText = '';

      if (item?.dataItem?.dataContext) {
        mainColor = item.dataItem.dataContext.color;
        color = Color.increaseBrightness(mainColor, 25);

        if (showPercents && typeof item.dataItem.dataContext.percent !== 'undefined') {
          percentText = ` ${Math.round(item.dataItem.dataContext.percent)}%`;
        }
      }

      return `<div style="${mainColor ? `background-color: ${color};` : ''} height: 20px; border-radius: 8px; min-width: 30px; line-height: 17px; padding: 2px 6px 3px 6px;">
      <div style="text-align: left; padding: 4px 10px 5px 10px; border-radius: 8px; line-height: 11px; display: inline-block; font-size: 14px; font-weight: 400;">
        ${html}${percentText}
      </div>
    </div>`;
    }
  );

  categoryAxis.renderer.labels.template.adapter.add('html', function () {
    return '{name}';
  });
}
